import { useSyncExternalStore } from 'react';
import { telegram } from './telegram';

/**
 * Tema tampilan (gelap/terang).
 *
 * Warna app TIDAK ditulis langsung di utility Tailwind melainkan lewat
 * CSS variable (`--c-ink-*`, `--c-neutral-*`, ...) yang nilainya ditukar di
 * `index.css` berdasarkan class `light`/`dark` pada <html>. Jadi ganti tema
 * = tukar satu class, tanpa perlu re-render atau varian `dark:` di ratusan
 * tempat.
 *
 * Pilihan user disimpan di localStorage. Kalau belum pernah memilih, tema
 * mengikuti Telegram (atau preferensi sistem di browser biasa).
 */
export type Theme = 'dark' | 'light';

const KEY = 'dongnime.theme';

/** Warna chrome (status bar / header Telegram / meta theme-color) per tema. */
const CHROME: Record<Theme, string> = {
  dark: '#0B0B0B',
  light: '#F6F3EE',
};

const listeners = new Set<() => void>();

/** Tema bawaan bila user belum pernah memilih. */
function preferred(): Theme {
  try {
    if (telegram.isInTelegram) return telegram.colorScheme;
  } catch {
    // SDK tidak tersedia — lanjut ke preferensi sistem.
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch {
    // matchMedia bisa tidak ada di webview lawas.
  }
  return 'dark';
}

function stored(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    // localStorage bisa throw (private mode).
  }
  return null;
}

let current: Theme = stored() ?? preferred();

/**
 * Tempelkan tema ke dokumen. Selain class untuk CSS variable, `color-scheme`
 * ikut di-set supaya kontrol NATIVE (tombol fullscreen video, scrollbar,
 * form) memakai warna yang senada — ini yang tidak bisa dilakukan CSS
 * variable sendirian.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', CHROME[theme]);
  telegram.setChromeColor(CHROME[theme]);
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(next: Theme): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // abaikan — state in-memory tetap update.
  }
  applyTheme(next);
  for (const l of listeners) l();
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, getTheme);
}
